/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { mercuryBaseQuery } from 'api/base'
import globalCookies from 'helpers/cookies'

import { appAPI } from 'api/app'
import { domainAPI } from 'api/domain'
import { supportAPI } from 'api/support'
import { shopAPI } from 'api/shop'
import { billingAPI } from 'api/billing'
import {
    AdditionalUser,
    EmailLog,
    SecurityQuestion
} from 'models/account'

import {
    CreateTwoFactorPayload,
    CreateTwoFactorResponse,
    TwoFactorMethodList
} from 'models/authentication'

/**********************************************************************************************************
 *   ACCOUNT API
 **********************************************************************************************************/
export const accountAPI = createApi({
    reducerPath: 'accountAPI',
    baseQuery: mercuryBaseQuery,
    tagTypes: ['User', 'Two-Factor', 'Additional-Users', 'Email-Logs', 'User-Sessions', 'Security'],
    endpoints: (builder) => ({
        updateProfile: builder.mutation({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const updateProfile = await baseQuery({
                    url: 'client/me',
                    method: 'PUT',
                    body: _arg
                })

                if (updateProfile.error) {
                    return { error: updateProfile.error }
                }

                const user: any = await baseQuery('client/me')

                if (user.error) {
                    return { error: user.error }
                }

                return {
                    data: user.data.data
                }
            }
        }),
        updatePassword: builder.mutation({
            query: (credentials) => ({
                url: 'client/me/password',
                method: 'PUT',
                body: credentials
            })
        }),
        changeEmailAddress: builder.mutation({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const changeEmailAddress = await baseQuery({
                    url: 'client/me/email',
                    method: 'PUT',
                    body: _arg
                })

                const user: any = await baseQuery('client/me')

                if (changeEmailAddress.error) {
                    return { error: changeEmailAddress.error }
                }

                if (user.error) {
                    return { error: user.error }
                }

                return {
                    data: user.data.data
                }
            }
        }),
        twoFactor: builder.query<TwoFactorMethodList, void>({
            query: () => 'client/me/two-factor',
            transformResponse: (response: { data: TwoFactorMethodList }) => response.data,
            providesTags: ['Two-Factor']
        }),
        createTwoFactor: builder.mutation<CreateTwoFactorResponse, CreateTwoFactorPayload>({
            query: (twoFactor) => ({
                url: 'client/me/two-factor/create',
                method: 'POST',
                body: twoFactor
            }),
            transformResponse: (response: { data: CreateTwoFactorResponse }) => response.data
        }),
        addTwoFactor: builder.mutation({
            query: (twoFactor) => ({
                url: 'client/me/two-factor',
                method: 'POST',
                body: twoFactor
            }),
            invalidatesTags: ['Two-Factor']
        }),
        updatePrimaryTwoFactor: builder.mutation({
            query: (id) => ({
                url: `client/me/two-factor/${id}`,
                method: 'PUT'
            }),
            invalidatesTags: ['Two-Factor']
        }),
        removeTwoFactor: builder.mutation({
            query: (id) => ({
                url: `client/me/two-factor/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Two-Factor']
        }),
        closeAccount: builder.mutation({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const closeAccount = await baseQuery({
                    url: 'client/close-account',
                    method: 'POST',
                    body: _arg
                })
                if (closeAccount.error) return { error: closeAccount.error }

                if (closeAccount.error) {
                    return { error: closeAccount.error }
                }

                const logout: any = await baseQuery({
                    url: 'client/logout',
                    method: 'POST'
                })

                if (logout.error) {
                    return { error: logout.error }
                }

                globalCookies.remove('XSRF-TOKEN')
                globalCookies.remove('mercury_session')

                _queryApi.dispatch(appAPI.util.resetApiState())
                _queryApi.dispatch(accountAPI.util.resetApiState())
                _queryApi.dispatch(domainAPI.util.resetApiState())
                _queryApi.dispatch(supportAPI.util.resetApiState())
                _queryApi.dispatch(shopAPI.util.resetApiState())
                _queryApi.dispatch(billingAPI.util.resetApiState())

                await baseQuery('client/csrf-token')

                return {
                    data: logout.data.data
                }
            },
            invalidatesTags: ['User']
        }),
        additionalUsers: builder.query<AdditionalUser[], void>({
            query: () => 'client/additional-users',
            transformResponse: (response: { data: AdditionalUser[] }) => response.data.filter(({ role: { name } }) => name !== 'account holder'),
            providesTags: ['Additional-Users']
        }),
        addAdditionalUser: builder.mutation({
            query: (user) => ({
                url: 'client/additional-users',
                method: 'POST',
                body: user
            }),
            invalidatesTags: ['Additional-Users']
        }),
        updateAdditionalUser: builder.mutation({
            query: ({ id, ...rest }) => ({
                url: `client/additional-users/${id}`,
                method: 'PUT',
                body: rest
            }),
            invalidatesTags: ['Additional-Users']
        }),
        removeAdditionalUser: builder.mutation({
            query: (id) => ({
                url: `client/additional-users/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Additional-Users']
        }),
        additionalUserRoles: builder.query({
            query: () => 'client/additional-users/roles'
        }),
        emailLogs: builder.query({
            query: (page = 1) => `client/email-logs?page=${page}`,
            providesTags: ['Email-Logs']
        }),
        viewEmailLogs: builder.query<EmailLog, number>({
            query: (id) => `client/email-logs/${id}`,
            transformResponse: (response: { data: EmailLog }) => response.data
        }),
        resendEmail: builder.mutation({
            query: (id) => ({
                url: `client/email-logs/${id}/resend`,
                method: 'POST'
            }),
            invalidatesTags: ['Email-Logs']
        }),
        getSecurity: builder.query<SecurityQuestion[], void>({
            query: () => 'client/questions',
            transformResponse: (response: { data: SecurityQuestion[] }) => response.data,
            providesTags: ['Security']
        }),
        updateSecurity: builder.mutation({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const security: any = await baseQuery({
                    url: 'client/me/question',
                    method: 'PUT',
                    body: _arg
                })

                if (security.error) {
                    return { error: security.error }
                }

                const user: any = await baseQuery('client/me')

                if (user.error) {
                    return { error: user.error }
                }

                return {
                    data: {
                        user: user.data.data,
                        security: security.data.data
                    }
                }
            }
        }),
        getUserSessions: builder.query({
            query: () => 'client/me/sessions',
            providesTags: ['User-Sessions']
        }),
        endUserSessions: builder.mutation({
            query: (body) => ({
                url: 'client/me/sessions',
                method: 'DELETE',
                body
            }),
            invalidatesTags: ['User-Sessions']
        })
    })
})

export const {
    useUpdateProfileMutation,
    useUpdatePasswordMutation,
    useChangeEmailAddressMutation,
    useTwoFactorQuery,
    useCreateTwoFactorMutation,
    useAddTwoFactorMutation,
    useUpdatePrimaryTwoFactorMutation,
    useRemoveTwoFactorMutation,
    useCloseAccountMutation,
    useAdditionalUsersQuery,
    useAddAdditionalUserMutation,
    useUpdateAdditionalUserMutation,
    useRemoveAdditionalUserMutation,
    useAdditionalUserRolesQuery,
    useEmailLogsQuery,
    useViewEmailLogsQuery,
    useResendEmailMutation,
    useGetSecurityQuery,
    useUpdateSecurityMutation,
    useGetUserSessionsQuery,
    useEndUserSessionsMutation
} = accountAPI
